import { PebblComponent } from './ai.models';

/*
 * Pure helpers for Pebbl `product_cards` and the other structured answer components.
 * No Angular imports on purpose: the live stream (ChatStreamStore) and the stored
 * transcript (PebblApiService.mapMessage) both go through these, so a reloaded reply
 * renders exactly what the streamed one did.
 *
 * Contract: gatherco-ai-v2 `product-cards-frontend-integration.md`. The storefront
 * (gatherco-frontend-v2 `core/utils/ai-chat-product-cards.ts`) implements the same rules;
 * keep placeProductCards() in step with it.
 */

/**
 * Normalise one backend component (streamed `component` frame or a stored
 * `components[]` entry) to the view-model, or null when it should not render.
 *
 * Unknown types return null rather than being coerced — a new component type must not
 * break the chat (it used to fall through to an empty `metric` tile). A `product_cards`
 * payload is kept only with a non-empty list of non-empty string SKUs, which are passed
 * through untouched: never trimmed, re-cased, sorted or de-duplicated.
 */
export function normalizePebblComponent(raw: unknown): PebblComponent | null {
  const c = raw as Record<string, any> | null;
  if (!c || typeof c !== 'object') return null;
  switch (c['type']) {
    case 'table':
      return {
        type: 'table',
        columns: c['columns'] ?? [],
        rows: c['rows'] ?? [],
        totalCount: c['total_count'] ?? c['totalCount'],
      };
    case 'metric':
      return { type: 'metric', label: c['label'], value: c['value'], unit: c['unit'] };
    case 'product_cards': {
      const skus = Array.isArray(c['skus'])
        ? (c['skus'] as unknown[]).filter((s): s is string => typeof s === 'string' && s.length > 0)
        : [];
      // `products` carries each SKU's `product_link`; a payload sent before it existed has none.
      // Its `recommended` flag marks the one product the reply recommends — the reply text no
      // longer says "Recommended", so the badge is driven by this flag alone.
      const linkBySku = new Map<string, string>();
      const recommendedSkus = new Set<string>();
      for (const product of Array.isArray(c['products']) ? (c['products'] as unknown[]) : []) {
        const p = product as Record<string, unknown> | null;
        if (!p || typeof p['sku'] !== 'string') continue;
        if (typeof p['product_link'] === 'string' && p['product_link']) linkBySku.set(p['sku'], p['product_link']);
        if (p['recommended'] === true) recommendedSkus.add(p['sku']);
      }
      const links = skus.map((sku) => linkBySku.get(sku) ?? null);
      const recommended = skus.map((sku) => recommendedSkus.has(sku));
      return skus.length ? { type: 'product_cards', skus, links, recommended } : null;
    }
    default:
      return null;
  }
}

/** Normalise a component list, dropping anything that should not render. */
export function normalizePebblComponents(raw: unknown[] | null | undefined): PebblComponent[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizePebblComponent).filter((c): c is PebblComponent => c !== null);
}

/** One carded product: its SKU, the `product_link` the reply links it by (null on old payloads)
 *  and whether the reply recommends it. */
export interface CardedProduct {
  sku: string;
  link: string | null;
  recommended: boolean;
}

/**
 * Every product from every `product_cards` component, in order. A second frame on the same
 * reply appends to the strip rather than replacing it.
 */
export function cardedProducts(components: PebblComponent[] | null | undefined): CardedProduct[] {
  const products: CardedProduct[] = [];
  for (const c of components ?? []) {
    if (c.type === 'product_cards') c.skus.forEach((sku, k) =>
        products.push({ sku, link: c.links?.[k] ?? null, recommended: c.recommended?.[k] === true }),
      );
  }
  return products;
}

/**
 * Which card each entry shows, by entry: the card whose `product_link` equals the entry's
 * link exactly, each card used once; null where none matches — a wrong product's picture
 * beside a name is worse than none. Matched on the link, never the position.
 *
 * A reply stored before `product_link` existed (every link null) falls back to the old rule:
 * the Nth entry takes the Nth card. Same rule as the storefront's pairEntries().
 */
export function pairEntries(hrefs: (string | null)[], links: (string | null)[]): (number | null)[] {
  if (!links.some((link) => !!link)) return hrefs.map((_, k) => (k < links.length ? k : null));
  const taken = new Set<number>();
  return hrefs.map((href) => {
    if (!href) return null;
    const match = links.findIndex((link, k) => !taken.has(k) && link === href);
    if (match < 0) return null;
    taken.add(match);
    return match;
  });
}

/** One run of a rendered reply: plain reply text, or one product's entry. */
export interface ReplyEntrySegment {
  /** Rendered HTML for the run. */
  html: string;
  /** This entry's number in the reply (pairEntries() finds its card by `href`); null for plain text. */
  entry: number | null;
  /** An entry directly under another entry, which draws the divider between the two. */
  followsEntry: boolean;
  /** The entry's product link (its name's href) — what its card is matched on. */
  href?: string;
  /** The entry's product name, as the link text reads. */
  name?: string;
}

/** How placeProductCards() decides what counts as an entry. */
export interface PlaceProductCardsOptions {
  /**
   * Count only entries whose name links to a GatherCo storefront product page. Used when a
   * reply carries no `product_cards` frame — the console's internal agent links every product
   * name to its stored storefront URL but sends no SKUs — so an invoice or admin link that
   * opens a line (`[O-WEB-3613](…)`) is never taken for a product.
   */
  storefrontOnly?: boolean;
  /**
   * The frame's `product_link`s. When any is set, only a name linking to one of them EXACTLY
   * opens an entry — a bold, linked line for something else (an order, a doc) stays plain
   * text. Empty on a reply stored before `product_link` existed, which keeps the looser rule.
   */
  links?: (string | null)[];
}

/** A GatherCo storefront product page: the public site with a non-empty path. */
export function isStorefrontProductLink(href: string | null | undefined): boolean {
  try {
    const url = new URL(href ?? '');
    return /^(www\.)?gatherco\.com\.au$/i.test(url.hostname) && url.pathname.replace(/\/+/g, '').length > 0;
  } catch {
    return false;
  }
}

/** A rendered reply cut into plain text and product entries. */
export interface ReplyEntryLayout {
  segments: ReplyEntrySegment[];
  /** How many entries were found (at most `skuCount`). */
  placed: number;
}

/**
 * Cut a rendered reply into plain text and product entries, so each product's picture sits
 * beside the entry that names it, not in a strip under the opening paragraph. Each entry's
 * card is found afterwards by its `href` (pairEntries()), never by its position.
 *
 * An entry is a paragraph, heading or list item that OPENS on a linked product name. The
 * agent writes that name line in several shapes, and all of them count:
 *
 *   **[Name](url)**                         the documented form
 *   **1.** [Name](url) **Recommended**      what it sends for a ranked shortlist
 *   1. **[Name](url)**                      as a numbered list item
 *   ### 1. [Name](url)                      as a heading
 *
 * Each entry is rebuilt as a name line (`p.md-entry-name`: ordinal, name and any short bold
 * tag after it, which becomes a badge) followed by the product's details. A name paragraph
 * followed directly by a list owns that list too, as it is the product's detail.
 *
 * Only the first `skuCount` entries are taken — there are never more pictures than cards. `firstEntry` offsets the entry indexes, for a reply rendered in several
 * segments (prose around an email draft). Works on HTML the markdown renderer already
 * produced, moving parsed nodes, so nothing new reaches the DOM beyond two class names.
 */
export function placeProductCards(
  html: string | null | undefined,
  skuCount: number,
  firstEntry = 0,
  options: PlaceProductCardsOptions = {},
): ReplyEntryLayout {
  const productLinks = new Set((options.links ?? []).filter((link): link is string => !!link));
  const opensOnProductName = (element: Element): boolean => {
    const link = productNameLink(element);
    const href = link?.getAttribute('href');
    // A search for a name Pebbl had no page for (linkItemNames()) is not a product page.
    if (!href || link!.classList.contains('md-search-link')) return false;
    // A storefront product page the frame skipped still opens an entry - the API cards only a
    // link that OPENS its line, so `**Recommended:** [AMARON](…)` came without one. Its entry
    // then looks its picture up by that link (ProductEntryComponent).
    if (productLinks.size) return productLinks.has(href) || isStorefrontProductLink(href);
    return !options.storefrontOnly || isStorefrontProductLink(href);
  };
  const isEntryBlock = (element: Element): boolean =>
    /^(P|H[1-6])$/.test(element.tagName) && opensOnProductName(element);
  const hasEntryItems = (list: Element): boolean =>
    Array.from(list.children).some((child) => child.tagName === 'LI' && opensOnProductName(child));

  const source = html ?? '';
  if (!source) return { segments: [], placed: 0 };
  if (skuCount < 1 || typeof document === 'undefined') {
    return { segments: [{ html: source, entry: null, followsEntry: false }], placed: 0 };
  }

  const template = document.createElement('template');
  template.innerHTML = source;

  const segments: ReplyEntrySegment[] = [];
  let plain: string[] = [];
  let placed = 0;

  // Consecutive plain blocks share one segment, so their margins collapse exactly as before.
  const flush = (): void => {
    if (plain.length) {
      segments.push({ html: plain.join(''), entry: null, followsEntry: false });
      plain = [];
    }
  };
  const pushEntry = (entryHtml: string, block: Element): void => {
    flush();
    const previous = segments[segments.length - 1];
    const link = productNameLink(block);
    segments.push({
      html: entryHtml,
      entry: firstEntry + placed,
      followsEntry: !!previous && previous.entry !== null,
      href: link?.getAttribute('href') ?? undefined,
      name: link?.textContent?.trim() || undefined,
    });
    placed++;
  };

  const nodes = Array.from(template.content.childNodes);
  for (let index = 0; index < nodes.length; index++) {
    const node = nodes[index];
    if (!(node instanceof Element)) {
      // marked puts a bare "\n" between blocks; as its own run it would split two
      // consecutive entries and drop the divider between them.
      if (node.textContent?.trim()) plain.push(escapeText(node.textContent));
      continue;
    }

    if (placed < skuCount && isEntryBlock(node)) {
      let entryHtml = buildEntry(node, null);
      let next = nextElement(nodes, index);
      // A heading name (`### 1. [Name](url)`) carries no text of its own, so the paragraph
      // under it is the product's description — without this it rendered outside the
      // entry, under View product. A paragraph name already holds its own details.
      if (/^H[1-6]$/.test(node.tagName) && next && next.element.tagName === 'P' && !isEntryBlock(next.element)) {
        entryHtml += next.element.outerHTML;
        index = next.index;
        next = nextElement(nodes, index);
      }
      if (next && isList(next.element) && !hasEntryItems(next.element)) {
        entryHtml += next.element.outerHTML;
        index = next.index;
      }
      pushEntry(entryHtml, node);
      continue;
    }

    if (placed < skuCount && isList(node) && hasEntryItems(node)) {
      const items = Array.from(node.children).filter((child) => child.tagName === 'LI');
      const first = node.tagName === 'OL' ? parseInt(node.getAttribute('start') ?? '1', 10) || 1 : null;
      let run: Element[] = [];
      let runStart = 0;
      items.forEach((item, position) => {
        if (placed < skuCount && opensOnProductName(item)) {
          if (run.length) {
            plain.push(listOf(node, run, runStart));
            run = [];
          }
          pushEntry(buildEntry(item, first === null ? null : first + position), item);
          return;
        }
        if (!run.length) runStart = position;
        run.push(item);
      });
      if (run.length) plain.push(listOf(node, run, runStart));
      continue;
    }

    plain.push(node.outerHTML);
  }
  flush();

  return { segments, placed };
}

/**
 * The next element after `index`, skipping the whitespace text marked puts between blocks
 * (`</p>\n<ul>`) — without the skip a name paragraph never finds the list that follows it.
 */
function nextElement(nodes: ChildNode[], index: number): { element: Element; index: number } | null {
  for (let k = index + 1; k < nodes.length; k++) {
    const node = nodes[k];
    if (node instanceof Element) return { element: node, index: k };
    if (node.textContent?.trim()) return null;
  }
  return null;
}

function isList(element: Element): boolean {
  return element.tagName === 'UL' || element.tagName === 'OL';
}

const ORDINAL = /^\s*\d+[.)]\s*$/;
const EMPHASIS = /^(STRONG|B|EM|I)$/;
const BLOCK = /^(UL|OL|P|DIV|DL|TABLE|BLOCKQUOTE|PRE|H[1-6])$/;

/**
 * The link the element opens on, past an optional ordinal ("1." as text or in bold) — bare,
 * or wrapped in bold/italic — or null. A sentence that only mentions a product ("I'd lead
 * with [Name](url)") starts with text, so it never counts.
 */
function productNameLink(element: Element): Element | null {
  let node = firstContent(entryChildren(element)[0] ?? null);
  if (isOrdinal(node)) node = firstContent(node!.nextSibling);
  if (isRecommendedLabel(node)) node = afterLabel(node!);
  while (node instanceof Element && EMPHASIS.test(node.tagName)) {
    node = firstContent(node.firstChild);
  }
  return node instanceof Element && node.tagName === 'A' ? node : null;
}

/**
 * The nodes an entry block is read from. A list item in a LOOSE list (items separated by blank
 * lines — how the agent spaces a shortlist with sub-bullets) has its text wrapped in a `<p>`:
 * `<li><p><a>Name</a></p><ul>…</ul></li>`. That paragraph is unwrapped so the name is found
 * and the detail list after it stays a detail block; otherwise the entry was never detected.
 */
function entryChildren(block: Element): ChildNode[] {
  const children = Array.from(block.childNodes);
  if (block.tagName !== 'LI') return children;
  const lead = firstContent(block.firstChild);
  if (!(lead instanceof Element) || lead.tagName !== 'P') return children;
  return [...Array.from(lead.childNodes), ...children.slice(children.indexOf(lead) + 1)];
}

/** `node` or the first sibling after it that is not blank text or a bare "1." number. */
function firstContent(node: ChildNode | null): ChildNode | null {
  let current = node;
  while (
    current &&
    current.nodeType === Node.TEXT_NODE &&
    (!current.textContent?.trim() || ORDINAL.test(current.textContent))
  ) {
    current = current.nextSibling;
  }
  return current;
}

/**
 * A bold/italic "Recommended:" the agent opens an entry with, before the name:
 * `- **Recommended:** [AMARON](url): limestone, …`. Without skipping it the entry was never
 * found, so the product lost its picture; buildEntry() turns it into the badge.
 */
function isRecommendedLabel(node: ChildNode | null): boolean {
  return (
    node instanceof Element &&
    EMPHASIS.test(node.tagName) &&
    !node.children.length &&
    RECOMMENDED_TAG.test(node.textContent ?? '')
  );
}

/** The first content after a label, past the blank text or ":" / dash that separates it. */
function afterLabel(label: ChildNode): ChildNode | null {
  let node = label.nextSibling;
  while (node && node.nodeType === Node.TEXT_NODE && /^[\s:–—-]*$/.test(node.textContent ?? '')) {
    node = node.nextSibling;
  }
  return node;
}

/** A bold run holding nothing but a number — the `**1.**` the agent ranks a shortlist with. */
function isOrdinal(node: ChildNode | null): boolean {
  return (
    node instanceof Element &&
    EMPHASIS.test(node.tagName) &&
    !node.children.length &&
    ORDINAL.test(node.textContent ?? '')
  );
}

/**
 * Rebuild one entry block as its name line and the details under it.
 *
 * The name line runs to the first line break or nested block, and ends at the product link
 * itself: details written on the same line (`[Name](url). Limestone, …`) start the detail
 * (splitAfterName()). Everything after is the detail. A list item's position becomes a plain "2." on the name line, so a split
 * shortlist keeps its numbering without an `<ol>` wrapper indenting the entry. The
 * "Recommended" badge comes from the frame's `recommended` flag (markRecommendedEntry()), or
 * from a bare "Recommended" tag the reply writes straight after the name (takeRecommendedTag());
 * any other bold text after the name stays ordinary text.
 */
function buildEntry(block: Element, ordinal: number | null): string {
  const name = document.createElement('p');
  name.className = 'md-entry-name';
  const details = document.createElement('p');
  const blocks: Node[] = [];

  let inName = true;
  for (const child of entryChildren(block)) {
    if (inName && child.nodeName === 'BR') {
      inName = false;
      continue;
    }
    if (child instanceof Element && BLOCK.test(child.tagName)) {
      inName = false;
      blocks.push(child.cloneNode(true));
      continue;
    }
    if (blocks.length && !(child.nodeType === Node.TEXT_NODE && !child.textContent?.trim())) {
      // Text after a nested block (rare) keeps its place after it rather than jumping above it.
      const tail = document.createElement('p');
      tail.appendChild(child.cloneNode(true));
      blocks.push(tail);
      continue;
    }
    // marked keeps a soft line break as "\n" text (no `breaks`), so a name line written
    // `**[Name](url)**\nDetails…` ends at that newline too.
    if (inName && child.nodeType === Node.TEXT_NODE && child.textContent?.includes('\n')) {
      const [head, ...tail] = child.textContent.split('\n');
      if (head) name.appendChild(document.createTextNode(head));
      inName = false;
      const rest = tail.join('\n');
      if (rest.trim()) details.appendChild(document.createTextNode(rest));
      continue;
    }
    (inName ? name : details).appendChild(child.cloneNode(true));
  }

  if (ordinal !== null && !/^\s*\d+[.)]/.test(name.textContent ?? '')) {
    name.insertBefore(document.createTextNode(`${ordinal}. `), name.firstChild);
  }

  // A leading "Recommended:" label (productNameLink() skipped it) becomes the badge.
  let labelled = false;
  let label = firstContent(name.firstChild);
  if (isOrdinal(label)) label = firstContent(label!.nextSibling);
  if (isRecommendedLabel(label)) {
    let gap = label!.nextSibling;
    while (gap && gap.nodeType === Node.TEXT_NODE && /^[\s:–—-]*$/.test(gap.textContent ?? '')) {
      const after = gap.nextSibling;
      gap.remove();
      gap = after;
    }
    label!.remove();
    labelled = true;
  }

  let lead = splitAfterName(name);
  trimEdges(details);
  // The reply's own "Recommended" tag, right after the name, becomes the badge.
  const first = lead ?? details;
  if (takeRecommendedTag(first) || labelled) {
    insertBadge(name);
    if (lead && !lead.childNodes.length) lead = null;
  }
  return (
    name.outerHTML +
    (lead ? lead.outerHTML : '') +
    (details.childNodes.length ? details.outerHTML : '') +
    blocks.map((node) => (node as Element).outerHTML).join('')
  );
}

/** The separator the agent puts between a product's name and its details: ". ", ", ", " – ". */
const NAME_SEPARATOR = /^\s*[.,;:–—-]?\s*/;

/**
 * End the name line at the product link and return what followed it as its own paragraph.
 *
 * The agent often writes a product as one line, `1. [Name](url). Limestone, Aged finish, …`,
 * with no break before the details. Left on the name line, the whole spec sentence rendered
 * bold, and the Recommended badge (block-level, set straight after the link) split it,
 * leaving an orphaned ". Limestone…" under the badge. A Range carries any bold wrapper
 * around the link over to the moved text, so `**1. [Name](url). Details**` stays bold.
 * Returns null when nothing but blank text or empty wrappers followed the link.
 */
function splitAfterName(name: Element): HTMLParagraphElement | null {
  const link = name.querySelector('a');
  if (!link || !name.lastChild) return null;
  const range = document.createRange();
  range.setStartAfter(link);
  range.setEndAfter(name.lastChild);
  if (!range.toString().trim()) {
    // Only blank text or an emptied wrapper - leave the name line as it was.
    return null;
  }
  const lead = document.createElement('p');
  lead.appendChild(range.extractContents());

  // Drop the separator the name line ended on, and start the details on a capital.
  const walker = document.createTreeWalker(lead, NodeFilter.SHOW_TEXT);
  for (let text = walker.nextNode(); text; text = walker.nextNode()) {
    if (!text.textContent?.trim()) continue;
    const rest = text.textContent.replace(NAME_SEPARATOR, '');
    text.textContent = rest.charAt(0).toUpperCase() + rest.slice(1);
    break;
  }
  // `**[Name](url)**: Details` leaves the bold wrapper's clone empty once its ":" is gone.
  lead.querySelectorAll('strong, b, em, i').forEach((el) => {
    if (!el.textContent?.trim()) el.remove();
  });
  trimEdges(lead);
  return lead;
}

/** A tag that only says the product is the pick: "Recommended", "(Recommended)", "Top recommendation". */
/** The same tag opening plain text, closed by brackets or a separator so a sentence never matches. */
const RECOMMENDED_TEXT_TAG = /^\s*(?:\(\s*(?:top\s+)?recommend(?:ed|ation)\s*\)\s*[:.–—-]?|(?:top\s+)?recommend(?:ed|ation)\s*[:–—-])\s*/i;
const RECOMMENDED_TAG = /^[\s(\[–—-]*(?:top\s+)?recommend(?:ed|ation)[\s)\]:.!–—-]*$/i;

/**
 * Take a "Recommended" tag off the start of the detail paragraph straight after an entry's
 * name, and report whether one was there. The reply sometimes writes the word itself
 * (`**[Name](url)** **Recommended**`, or on the next line) instead of leaving it to the
 * frame's `recommended` flag; left as text it rendered as a plain bold line where the badge
 * belongs. Only a whole paragraph or a leading bold/italic run that says nothing else
 * counts, so a sentence that merely uses the word is left alone.
 */
function takeRecommendedTag(paragraph: Element): boolean {
  if (RECOMMENDED_TAG.test(paragraph.textContent ?? '')) {
    paragraph.replaceChildren();
    return true;
  }
  let lead = paragraph.firstChild;
  while (lead && lead.nodeType === Node.TEXT_NODE && !lead.textContent?.trim()) lead = lead.nextSibling;
  // Plain text: "(Recommended): Limestone…" or "Recommended – Limestone…", never "Recommended for…".
  if (lead?.nodeType === Node.TEXT_NODE) {
    const text = lead.textContent ?? '';
    const tag = text.match(RECOMMENDED_TEXT_TAG);
    if (!tag) return false;
    const rest = text.slice(tag[0].length);
    lead.textContent = rest.charAt(0).toUpperCase() + rest.slice(1);
    trimEdges(paragraph);
    return true;
  }
  if (!(lead instanceof Element) || !EMPHASIS.test(lead.tagName) || !RECOMMENDED_TAG.test(lead.textContent ?? '')) {
    return false;
  }
  lead.remove();
  const next = paragraph.firstChild;
  if (next?.nodeType === Node.TEXT_NODE) {
    const rest = (next.textContent ?? '').replace(NAME_SEPARATOR, '');
    next.textContent = rest.charAt(0).toUpperCase() + rest.slice(1);
  }
  trimEdges(paragraph);
  return true;
}

/** Set the badge straight after the name (the run holding its link); once per name line. */
function insertBadge(name: Element): void {
  if (name.querySelector('.md-entry-badge')) return;
  const badge = document.createElement('strong');
  badge.className = 'md-entry-badge';
  badge.textContent = 'Recommended';
  // Straight after the name (the run holding its link), where the text-derived badge sat.
  const holder = Array.from(name.childNodes).find(
    (child) => child instanceof Element && (child.tagName === 'A' || !!child.querySelector('a')),
  );
  name.insertBefore(badge, holder?.nextSibling ?? null);
  name.insertBefore(document.createTextNode(' '), badge);
}

/**
 * Add the "Recommended" badge to an entry's name line — for the entry whose card carries
 * `recommended: true`. Same element and class the badge has always had, so it looks the same.
 */
export function markRecommendedEntry(html: string): string {
  if (typeof document === 'undefined') return html;
  const template = document.createElement('template');
  template.innerHTML = html;
  const name = template.content.querySelector('p.md-entry-name');
  if (!name || name.querySelector('.md-entry-badge')) return html;
  insertBadge(name);
  return template.innerHTML;
}

/** Drop leading/trailing blank text and line breaks, which would open an empty first line. */
function trimEdges(element: Element): void {
  const isBlank = (node: ChildNode | null): boolean =>
    !!node && (node.nodeName === 'BR' || (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()));
  while (isBlank(element.firstChild)) element.firstChild!.remove();
  while (isBlank(element.lastChild)) element.lastChild!.remove();
}

/** A copy of `list` holding only `items`, numbered from the first item's original position. */
function listOf(list: Element, items: Element[], position: number): string {
  const copy = list.cloneNode(false) as Element;
  if (list.tagName === 'OL') {
    const start = (parseInt(list.getAttribute('start') ?? '1', 10) || 1) + position;
    if (start !== 1) copy.setAttribute('start', String(start));
  }
  items.forEach((item) => copy.appendChild(item.cloneNode(true)));
  return copy.outerHTML;
}

function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
